import {
  loadStripe,
  Stripe,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';
import { IInputFocusEvent, PaymentMethodDlocalRef } from 'components/molecules/Payment-method-dlocal/models/payment-method-dlocal.interface';
import { PaymentMethodStripeRef } from 'components/molecules/Payment-method-stripe/models/Payment-method-stripe';
import { useFormik } from 'formik';
import { IAppReducer } from 'models';
import {
  IFormAddPaymentMethod,
  IFormErrorsAddPaymentMethod,
} from 'pages/Add-payment-method/models/Add-payment-method.interface';
import { Ref, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { addUserCard, addUserCardLink } from 'shared/services/user';
import { getKeyStripe } from 'utils/branch.utils';

interface UsePaymentMethodFormReturn {
  stripeLoad: Stripe | null;
  formAddPaymentMethod: ReturnType<typeof useFormik<IFormAddPaymentMethod>>
  user: { uid: string; formToShow?: string }
  handleChangeCheck: (event: boolean | undefined) => void;
  changeValueInput: (
    event:
      | StripeCardNumberElementChangeEvent
      | StripeCardExpiryElementChangeEvent
      | StripeCardCvcElementChangeEvent
      | IInputFocusEvent
  ) => void
  stripeRef: React.RefObject<PaymentMethodStripeRef>
  dlocalRef: Ref<PaymentMethodDlocalRef> | undefined
  branch: string
}

interface UsePaymentMethodFormProps {
  userAuthId?: string;
  handleRefreshCards?: () => void;
  handleShowNewCardForm?: (value: boolean) => void;
  handleError?: (value: string | null) => void;
  isPaymentMethodLink: boolean
  setPopupState?: React.Dispatch<React.SetStateAction<{
    isOpen: boolean;
    type: string;
    msg: string;
  }>>
}

export const usePaymentMethodForm = ({
  userAuthId,
  isPaymentMethodLink,
  handleRefreshCards,
  handleShowNewCardForm,
  handleError,
  setPopupState
}: UsePaymentMethodFormProps): UsePaymentMethodFormReturn => {
  const branch = useSelector(
    (state: IAppReducer) => state.branchReducer.branch
  );

  const user = useSelector((state: IAppReducer) => state.authReducer);
  const stripeRef = useRef<PaymentMethodStripeRef>(null);
  const dlocalRef =
    useRef<{ getTokenCard: () => Promise<string> } | null>(null);
  const [stripeLoad, setStripeLoad] = useState<Stripe | null>(null);

  useLayoutEffect(() => {
    const loadStripeElement = async () => {
      const stripeKey = getKeyStripe(branch);
      const stripe = (await loadStripe(stripeKey)) as Stripe;
      setStripeLoad(stripe);
    };
    loadStripeElement();
  }, [setStripeLoad]);

  const changeValueInput = (
    event:
      | StripeCardNumberElementChangeEvent
      | StripeCardExpiryElementChangeEvent
      | StripeCardCvcElementChangeEvent
      | IInputFocusEvent
  ) => {
    let value = '';
    if (event.complete) {
      value = 'complete';
    }
    let element: 'cardCvc' | 'cardExpiry' | 'cardNumber';
    if ('elementType' in event) {
      element = event.elementType;
    } else {
      element = event.element;
    }
    formAddPaymentMethod.setFieldValue(element, value);
    formAddPaymentMethod.values[element] = value;
  };

  const handleChangeCheck = (event: boolean | undefined) => {
    formAddPaymentMethod.setFieldValue('isPrincipal', !event);
  };
  const formAddPaymentMethodInitialValues: IFormAddPaymentMethod = {
    name: '',
    isPrincipal: false,
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
  };

  const formAddPaymentMethodValidate = (values: IFormAddPaymentMethod) => {
    const errors: IFormErrorsAddPaymentMethod = {};
    if (!values.name) {
      errors.name = 'Campo requerido';
    }
    if (!values.cardNumber) {
      errors.cardNumber = 'Campo requerido';
    }
    if (!values.cardExpiry) {
      errors.cardExpiry = 'Campo requerido';
    }
    if (!values.cardCvc) {
      errors.cardCvc = 'Campo requerido';
    }
    return errors;
  };

  const formAddPaymentMethod = useFormik({
    initialValues: formAddPaymentMethodInitialValues,
    validate: formAddPaymentMethodValidate,
    onSubmit: async (values) => {
      try {
        let userTemp = {
          uid: user.uid,
          formToShow: user.formToShow
        };
        if(isPaymentMethodLink && userAuthId) {
          userTemp = {
            uid: userAuthId,
            formToShow: 'stripe'
          }
        }
        let token = '';
        if (
          branch === 'pr' ||
          !userTemp.formToShow ||
          userTemp.formToShow.toLocaleLowerCase() === 'stripe'
        ) {
          if (stripeRef.current) {
            const tokenCard = await stripeRef.current.getTokenCard();
            if (!tokenCard) {
              throw new Error('Failed to get token from Stripe');
            }
            token = tokenCard;
          }
        } else {
          if (dlocalRef.current) {
            token = await dlocalRef.current.getTokenCard();
          } else {
            throw new Error('dlocalRef is null');
          }
        }

        if(isPaymentMethodLink && setPopupState) {
          await addUserCardLink(userTemp.uid, token, values.isPrincipal, {
            userImpersonationId: user.agentAuthData?.userId,
            sourceApp: user.agentAuthData?.sourceApp ?? 'meraki-frontend',
            sourceOrigin: user.agentAuthData?.sourceOrigin ?? (window.location.origin + '/'),
          });
          setPopupState({
            isOpen: true,
            type: 'success',
            msg: 'Tarjeta agregada con éxito',
          });
        } else {
          await addUserCard(userTemp.uid, token, values.isPrincipal, {
            userImpersonationId: user.agentAuthData?.userId,
            sourceApp: user.agentAuthData?.sourceApp ?? 'meraki-frontend',
            sourceOrigin: user.agentAuthData?.sourceOrigin ?? (window.location.origin + '/'),
          });
        }
        if (handleRefreshCards) handleRefreshCards();
        if (handleShowNewCardForm) handleShowNewCardForm(false);
      } catch (error) {
        console.log(error);
        if (handleError) handleError('Error al crear método de pago');
      } finally {
        formAddPaymentMethod.setSubmitting(false);
      }
    },
  });

  return {
    stripeLoad,
    formAddPaymentMethod,
    user,
    handleChangeCheck,
    changeValueInput,
    stripeRef,
    dlocalRef,
    branch
  };
};
